import { BreakpointObserver } from '@angular/cdk/layout'
import { afterNextRender, Component, DestroyRef, effect, EventEmitter, inject, Injector, input, Output, runInInjectionContext, ViewChild, viewChild } from '@angular/core'
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop'
import { FormControl } from '@angular/forms'
import { DropdownComponent } from '@app/v2/shared/components/dropdown/dropdown.component'
import { ComponentWithDataSetter } from '@app/v2/shared/components/dropdown/types/dropdown-option.type'
import { BasicInputComponent } from '@app/v2/shared/components/inputs/basic-input/basic-input.component'
import { ModalComponent } from '@app/v2/shared/components/modal/modal.component'
import { BasicFieldType } from '@app/v2/shared/enums/field.enum'
import { SafeDomPipe } from '@app/v2/shared/pipes/safe-dom.pipe'
import { ModalService } from '@app/v2/shared/services/modal.service'
import { map } from 'rxjs'
import { DateRange, DateRangePickerContentComponent, DisabledYearMonthDayRange } from './date-range-picker-content/date-range-picker-content.component'

/** Material “link” glyph used for open-ended (indefinite) end date in trigger + modal summary. */
const INDEFINITE_END_ICON_SVG = `<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 -960 960 960" fill="#111928" focusable="false" aria-hidden="true"><path d="M220-260q-92 0-156-64T0-480q0-92 64-156t156-64q37 0 71 13t61 37l68 62-60 54-62-56q-16-14-36-22t-42-8q-58 0-99 41t-41 99q0 58 41 99t99 41q22 0 42-8t36-22l310-280q27-24 61-37t71-13q92 0 156 64t64 156q0 92-64 156t-156 64q-37 0-71-13t-61-37l-68-62 60-54 62 56q16 14 36 22t42 8q58 0 99-41t41-99q0-58-41-99t-99-41q-22 0-42 8t-36 22L352-310q-27 24-61 37t-71 13Z"/></svg>`

/** Calendar icon (same paths as default text trigger) for basic-input trigger. */
const CALENDAR_TRIGGER_ICON_SVG = `<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M0 14.4C0 14.8243 0.168571 15.2313 0.468629 15.5314C0.768687 15.8314 1.17565 16 1.6 16H14.4C14.8243 16 15.2313 15.8314 15.5314 15.5314C15.8314 15.2313 16 14.8243 16 14.4V6.4H0V14.4ZM11.2 8.4C11.2 8.29391 11.2421 8.19217 11.3172 8.11716C11.3922 8.04214 11.4939 8 11.6 8H12.4C12.5061 8 12.6078 8.04214 12.6828 8.11716C12.7579 8.19217 12.8 8.29391 12.8 8.4V9.2C12.8 9.30609 12.7579 9.40783 12.6828 9.48284C12.6078 9.55786 12.5061 9.6 12.4 9.6H11.6C11.4939 9.6 11.3922 9.55786 11.3172 9.48284C11.2421 9.40783 11.2 9.30609 11.2 9.2V8.4ZM11.2 11.6C11.2 11.4939 11.2421 11.3922 11.3172 11.3172C11.3922 11.2421 11.4939 11.2 11.6 11.2H12.4C12.5061 11.2 12.6078 11.2421 12.6828 11.3172C12.7579 11.3922 12.8 11.4939 12.8 11.6V12.4C12.8 12.5061 12.7579 12.6078 12.6828 12.6828C12.6078 12.7579 12.5061 12.8 12.4 12.8H11.6C11.4939 12.8 11.3922 12.7579 11.3172 12.6828C11.2421 12.6078 11.2 12.5061 11.2 12.4V11.6ZM7.2 8.4C7.2 8.29391 7.24214 8.19217 7.31716 8.11716C7.39217 8.04214 7.49391 8 7.6 8H8.4C8.50609 8 8.60783 8.04214 8.68284 8.11716C8.75786 8.19217 8.8 8.29391 8.8 8.4V9.2C8.8 9.30609 8.75786 9.40783 8.68284 9.48284C8.60783 9.55786 8.50609 9.6 8.4 9.6H7.6C7.49391 9.6 7.39217 9.55786 7.31716 9.48284C7.24214 9.40783 7.2 9.30609 7.2 9.2V8.4ZM7.2 11.6C7.2 11.4939 7.24214 11.3922 7.31716 11.3172C7.39217 11.2421 7.49391 11.2 7.6 11.2H8.4C8.50609 11.2 8.60783 11.2421 8.68284 11.3172C8.75786 11.3922 8.8 11.4939 8.8 11.6V12.4C8.8 12.5061 8.75786 12.6078 8.68284 12.6828C8.60783 12.7579 8.50609 12.8 8.4 12.8H7.6C7.49391 12.8 7.39217 12.7579 7.31716 12.6828C7.24214 12.6078 7.2 12.5061 7.2 12.4V11.6ZM3.2 8.4C3.2 8.29391 3.24214 8.19217 3.31716 8.11716C3.39217 8.04214 3.49391 8 3.6 8H4.4C4.50609 8 4.60783 8.04214 4.68284 8.11716C4.75786 8.19217 4.8 8.29391 4.8 8.4V9.2C4.8 9.30609 4.75786 9.40783 4.68284 9.48284C4.60783 9.55786 4.50609 9.6 4.4 9.6H3.6C3.49391 9.6 3.39217 9.55786 3.31716 9.48284C3.24214 9.40783 3.2 9.30609 3.2 9.2V8.4ZM3.2 11.6C3.2 11.4939 3.24214 11.3922 3.31716 11.3172C3.39217 11.2421 3.49391 11.2 3.6 11.2H4.4C4.50609 11.2 4.60783 11.2421 4.68284 11.3172C4.75786 11.3922 4.8 11.4939 4.8 11.6V12.4C4.8 12.5061 4.75786 12.6078 4.68284 12.6828C4.60783 12.7579 4.50609 12.8 4.4 12.8H3.6C3.49391 12.8 3.39217 12.7579 3.31716 12.6828C3.24214 12.6078 3.2 12.5061 3.2 12.4V11.6Z" fill="#6B7280"/><path d="M16 3.2C16 2.77565 15.8314 2.36869 15.5314 2.06863C15.2313 1.76857 14.8243 1.6 14.4 1.6H12.8V0.8C12.8 0.587827 12.7157 0.384344 12.5657 0.234315C12.4157 0.0842854 12.2122 0 12 0C11.7878 0 11.5843 0.0842854 11.4343 0.234315C11.2843 0.384344 11.2 0.587827 11.2 0.8V1.6H8.8V0.8C8.8 0.587827 8.71571 0.384344 8.56569 0.234315C8.41566 0.0842854 8.21217 0 8 0C7.78783 0 7.58434 0.0842854 7.43431 0.234315C7.28429 0.384344 7.2 0.587827 7.2 0.8V1.6H4.8V0.8C4.8 0.587827 4.71571 0.384344 4.56569 0.234315C4.41566 0.0842854 4.21217 0 4 0C3.78783 0 3.58434 0.0842854 3.43431 0.234315C3.28429 0.384344 3.2 0.587827 3.2 0.8V1.6H1.6C1.17565 1.6 0.768687 1.76857 0.468629 2.06863C0.168571 2.36869 0 2.77565 0 3.2V4.8H16V3.2Z" fill="#6B7280"/></svg>`

@Component({
  selector: 'app-date-range-picker',
  standalone: true,
  imports: [DropdownComponent, BasicInputComponent, DateRangePickerContentComponent, ModalComponent, SafeDomPipe],
  templateUrl: './date-range-picker.component.html',
  styleUrl: './date-range-picker.component.scss'
})
export class DateRangePickerComponent {
  fieldType = input<BasicFieldType>(BasicFieldType.DATE)
  startDate = input<string | null>(null)
  endDate = input<string | null>(null)
  /** When set, pre-checks "My event runs indefinitely" to match event `is_indefinitely` (overrides inferring from end date alone). */
  isIndefinitely = input<boolean | undefined>(undefined)
  /** When `false`, hides “My event runs indefinitely” and keeps the range finite-only. */
  showIndefiniteOption = input(true)
  /** Earliest selectable calendar day (`YYYY-MM-DD`). Used e.g. for timing exceptions after the event public start date. */
  minSelectableDate = input<string | null>(null)
  /** Latest selectable calendar day (`YYYY-MM-DD`). Used e.g. for archived-item filters that only allow past dates. */
  maxSelectableDate = input<string | null>(null)
  /**
   * `YYYY-MM-DD` — left calendar opens on this month (e.g. event public start) when provided;
   * otherwise the month follows the selected start/end dates, then today.
   */
  initialVisibleMonthDate = input<string | null>(null)
  /** Inclusive `YYYY-MM-DD` ranges where every day is disabled (e.g. dates already used by other exceptions). */
  disabledDateRanges = input<DisabledYearMonthDayRange[]>([])
  /** Inclusive `YYYY-MM-DD` ranges where days are disabled WITHOUT the "Closed" highlight. */
  silentDisabledDateRanges = input<DisabledYearMonthDayRange[]>([])
  /**
   * When set, calendar dates whose weekday is NOT in this list are disabled and highlighted.
   * Used by timing exceptions so users can only pick dates that match the event’s selected weekdays.
   */
  allowedWeekDays = input<string[] | null>(null)
  /**
   * When `true`, shows a read-only `basic-input` as the trigger (Figma-style “Date” field).
   * Clicks/focus open the calendar; Apply still emits `rangeSelected` and updates the displayed text.
   */
  useBasicInputTrigger = input(false)
  basicInputLabel = input('Date')
  basicInputPlaceholder = input('Date')
  basicInputId = input('date-range-picker-input')
  /** When `true`, shows the required asterisk on the basic-input label (use with validators on `displayControlOverride`). */
  basicInputRequired = input(false)
  /**
   * When set (e.g. from a parent form), the basic-input trigger binds to this control so validators and errors live on the parent.
   */
  displayControlOverride = input<FormControl<string> | null>(null)
  /**
   * When `true`, the dropdown calendar shows a single month on narrow viewports (max-width: 768px);
   * use e.g. in modals on mobile so the picker is not two side-by-side months.
   */
  compactCalendarOnMobile = input(false)
  /**
   * When `true`, viewports ≤768px open the picker in a full-screen style modal (Figma “Set event dates”)
   * instead of the floating dropdown.
   */
  useModalOnMobile = input(false)
  /** Modal header when {@link useModalOnMobile} is active. */
  mobileModalTitle = input('Set event dates')

  @Output() rangeSelected = new EventEmitter<DateRange>()

  @ViewChild('dropdownRef') dropdownRef!: DropdownComponent

  /** Sanitized in template via {@link SafeDomPipe}. */
  readonly indefiniteEndIconSvg = INDEFINITE_END_ICON_SVG

  readonly calendarTriggerIcon = CALENDAR_TRIGGER_ICON_SVG

  /** Unique id for `ModalService` / `<modal [id]>`. */
  readonly mobileModalId =
    typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? `date-range-picker-mobile-${crypto.randomUUID()}`
      : `date-range-picker-mobile-${Math.random().toString(36).slice(2, 11)}`

  /** Calendar icon for modal header (`innerHTML` via `ModalComponent`). */
  readonly mobileModalHeaderIcon = `<svg width="20" height="20" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M0 16.2C0 16.6774 0.189642 17.1352 0.527208 17.4728C0.864773 17.8104 1.32261 18 1.8 18H16.2C16.6774 18 17.1352 17.8104 17.4728 17.4728C17.8104 17.1352 18 16.6774 18 16.2V7.2H0V16.2ZM12.6 9.45C12.6 9.33065 12.6474 9.21619 12.7318 9.1318C12.8162 9.04741 12.9307 9 13.05 9H13.95C14.0693 9 14.1838 9.04741 14.2682 9.1318C14.3526 9.21619 14.4 9.33065 14.4 9.45V10.35C14.4 10.4693 14.3526 10.5838 14.2682 10.6682C14.1838 10.7526 14.0693 10.8 13.95 10.8H13.05C12.9307 10.8 12.8162 10.7526 12.7318 10.6682C12.6474 10.5838 12.6 10.4693 12.6 10.35V9.45ZM12.6 13.05C12.6 12.9307 12.6474 12.8162 12.7318 12.7318C12.8162 12.6474 12.9307 12.6 13.05 12.6H13.95C14.0693 12.6 14.1838 12.6474 14.2682 12.7318C14.3526 12.8162 14.4 12.9307 14.4 13.05V13.95C14.4 14.0693 14.3526 14.1838 14.2682 14.2682C14.1838 14.3526 14.0693 14.4 13.95 14.4H13.05C12.9307 14.4 12.8162 14.3526 12.7318 14.2682C12.6474 14.1838 12.6 14.0693 12.6 13.95V13.05ZM8.1 9.45C8.1 9.33065 8.14741 9.21619 8.2318 9.1318C8.31619 9.04741 8.43065 9 8.55 9H9.45C9.56935 9 9.68381 9.04741 9.7682 9.1318C9.85259 9.21619 9.9 9.33065 9.9 9.45V10.35C9.9 10.4693 9.85259 10.5838 9.7682 10.6682C9.68381 10.7526 9.56935 10.8 9.45 10.8H8.55C8.43065 10.8 8.31619 10.7526 8.2318 10.6682C8.14741 10.5838 8.1 10.4693 8.1 10.35V9.45ZM8.1 13.05C8.1 12.9307 8.14741 12.8162 8.2318 12.7318C8.31619 12.6474 8.43065 12.6 8.55 12.6H9.45C9.56935 12.6 9.68381 12.6474 9.7682 12.7318C9.85259 12.8162 9.9 12.9307 9.9 13.05V13.95C9.9 14.0693 9.85259 14.1838 9.7682 14.2682C9.68381 14.3526 9.56935 14.4 9.45 14.4H8.55C8.43065 14.4 8.31619 14.3526 8.2318 14.2682C8.14741 14.1838 8.1 14.0693 8.1 13.95V13.05ZM3.6 9.45C3.6 9.33065 3.64741 9.21619 3.7318 9.1318C3.81619 9.04741 3.93065 9 4.05 9H4.95C5.06935 9 5.18381 9.04741 5.2682 9.1318C5.35259 9.21619 5.4 9.33065 5.4 9.45V10.35C5.4 10.4693 5.35259 10.5838 5.2682 10.6682C5.18381 10.7526 5.06935 10.8 4.95 10.8H4.05C3.93065 10.8 3.81619 10.7526 3.7318 10.6682C3.64741 10.5838 3.6 10.4693 3.6 10.35V9.45ZM3.6 13.05C3.6 12.9307 3.64741 12.8162 3.7318 12.7318C3.81619 12.6474 3.93065 12.6 4.05 12.6H4.95C5.06935 12.6 5.18381 12.6474 5.2682 12.7318C5.35259 12.8162 5.4 12.9307 5.4 13.05V13.95C5.4 14.0693 5.35259 14.1838 5.2682 14.2682C5.18381 14.3526 5.06935 14.4 4.95 14.4H4.05C3.93065 14.4 3.81619 14.3526 3.7318 14.2682C3.64741 14.1838 3.6 14.0693 3.6 13.95V13.05Z" fill="#111928"/><path d="M18 3.6C18 3.12261 17.8104 2.66477 17.4728 2.32721C17.1352 1.98964 16.6774 1.8 16.2 1.8H14.4V0.9C14.4 0.661305 14.3052 0.432387 14.1364 0.263604C13.9676 0.0948211 13.7387 0 13.5 0C13.2613 0 13.0324 0.0948211 12.8636 0.263604C12.6948 0.432387 12.6 0.661305 12.6 0.9V1.8H9.9V0.9C9.9 0.661305 9.80518 0.432387 9.6364 0.263604C9.46761 0.0948211 9.23869 0 9 0C8.7613 0 8.53239 0.0948211 8.3636 0.263604C8.19482 0.432387 8.1 0.661305 8.1 0.9V1.8H5.4V0.9C5.4 0.661305 5.30518 0.432387 5.1364 0.263604C4.96761 0.0948211 4.73869 0 4.5 0C4.26131 0 4.03239 0.0948211 3.8636 0.263604C3.69482 0.432387 3.6 0.661305 3.6 0.9V1.8H1.8C1.32261 1.8 0.864773 1.98964 0.527208 2.32721C0.189642 2.66477 0 3.12261 0 3.6V5.4H18V3.6Z" fill="#111928"/></svg>`

  /** True at ≤768px; used with {@link useModalOnMobile}. */
  readonly isMobileLayout = toSignal(
    inject(BreakpointObserver)
      .observe('(max-width: 768px)')
      .pipe(map((r) => r.matches)),
    {
      initialValue: typeof window !== 'undefined' && window.matchMedia('(max-width: 768px)').matches
    }
  )

  private readonly _modalPickerContent = viewChild<DateRangePickerContentComponent>('modalPickerContent')
  private readonly _injector = inject(Injector)
  private readonly _modalService = inject(ModalService)
  private readonly _destroyRef = inject(DestroyRef)
  private readonly _internalDisplayControl = new FormControl('', { nonNullable: true })
  private _mobilePickerModalWasOpen = false

  constructor() {
    effect(() => {
      if (!this.useBasicInputTrigger()) return
      this.startDate()
      this.endDate()
      this.isIndefinitely()
      this.displayControlOverride()
      this._syncDisplayControlFromRange()
    })

    effect(() => {
      this._modalService.modalState$.pipe(takeUntilDestroyed(this._destroyRef)).subscribe((state) => {
        const open = !!state[this.mobileModalId]
        if (open) {
          if (!this._mobilePickerModalWasOpen) {
            this._mobilePickerModalWasOpen = true
            runInInjectionContext(this._injector, () => {
              afterNextRender(() => {
                const inst = this._modalPickerContent()
                if (!inst) return
                this._wirePickerContentInstance(inst, { forceSingleMonth: true })
                inst.init()
              })
            })
          }
        } else {
          this._mobilePickerModalWasOpen = false
        }
      })
    })

    effect(() => {
      if (!this.isMobileLayout() && this._modalService.isModalOpen(this.mobileModalId)) {
        this._modalService.toggleModal(this.mobileModalId)
      }
    })
  }

  /** Whether the mobile modal layout path is active (modal instead of dropdown). */
  mobileModalPathActive(): boolean {
    return this.useModalOnMobile() && this.isMobileLayout()
  }

  openMobileModal(event?: Event): void {
    event?.stopPropagation()
    event?.preventDefault()
    if (!this._modalService.isModalOpen(this.mobileModalId)) {
      this._modalService.toggleModal(this.mobileModalId)
    }
  }

  /** Summary row under the modal title (Figma). */
  modalSummaryStartText(): string {
    const startDate = this.startDate()
    if (startDate == null || startDate === '') return 'Select start'
    return this._formatDate(startDate)
  }

  modalSummaryEndText(): string {
    if (this._isOpenEndedRange()) return ''
    const endDate = this.endDate()
    if (endDate == null || endDate === '') return 'Select end'
    return this._formatDate(endDate)
  }

  /** Open-ended range: show link icon instead of “No end date” copy in the trigger / modal. */
  isIndefiniteEndDisplay(): boolean {
    return this._isOpenEndedRange()
  }

  formattedStartForDisplay(): string {
    const startDate = this.startDate()
    if (startDate == null || startDate === '') return ''
    return this._formatDate(startDate)
  }

  hasDistinctRangeEnd(): boolean {
    const endDate = this.endDate()
    const startDate = this.startDate()
    return !!(endDate && endDate !== '' && endDate !== startDate)
  }

  formattedEndForDisplay(): string {
    const endDate = this.endDate()
    if (!endDate) return ''
    return this._formatDate(endDate)
  }

  /** For summary styling: not the muted “Select end” placeholder. */
  summaryEndHasConcreteDate(): boolean {
    if (this._isOpenEndedRange()) return true
    const endDate = this.endDate()
    return !!(endDate && endDate !== '')
  }

  /** Control passed to `basic-input` when `useBasicInputTrigger` is true. */
  get displayControlForBasicInput(): FormControl<string> {
    return this.displayControlOverride() ?? this._internalDisplayControl
  }

  /** Opens dropdown (desktop) or mobile modal when {@link useModalOnMobile} is enabled. */
  openPickerFromTrigger(event?: Event): void {
    if (this.useModalOnMobile() && this.isMobileLayout()) {
      this.openMobileModal(event)
      return
    }
    this.dropdownRef?.openDropdown(event)
  }

  /** Matches parent `is_indefinitely`: no end date in the model. */
  private _isOpenEndedRange(): boolean {
    const isIndefinitely = this.isIndefinitely()
    const endDate = this.endDate()
    if (isIndefinitely === true) return true
    if (isIndefinitely === false) return false
    return endDate == null || endDate === ''
  }

  private _syncDisplayControlFromRange(): void {
    const startDate = this.startDate()
    const endDate = this.endDate()
    const text =
      startDate == null || startDate === ''
        ? ''
        : this._isOpenEndedRange()
        ? `${this._formatDate(startDate)} - \u221E`
        : !endDate || endDate === startDate
        ? this._formatDate(startDate)
        : `${this._formatDate(startDate)} - ${this._formatDate(endDate)}`
    this.displayControlForBasicInput.setValue(text, { emitEvent: false })
  }

  private _formatDate(ymd: string): string {
    const d = new Date(ymd + 'T12:00:00')
    if (isNaN(d.getTime())) return ymd
    return d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })
  }

  private _closePickerUi(): void {
    if (this._modalService.isModalOpen(this.mobileModalId)) {
      this._modalService.toggleModal(this.mobileModalId)
    } else {
      this.dropdownRef?.close()
    }
  }

  private _wirePickerContentInstance(instance: DateRangePickerContentComponent, options?: { forceSingleMonth: boolean }): void {
    instance.initialStartDate.set(this.startDate())
    instance.initialEndDate.set(this.endDate())
    instance.initialIsIndefinitely.set(this.isIndefinitely())
    instance.showIndefiniteOption.set(this.showIndefiniteOption())
    instance.minSelectableDate.set(this.minSelectableDate())
    instance.maxSelectableDate.set(this.maxSelectableDate())
    instance.initialVisibleMonthDate.set(this.initialVisibleMonthDate())
    instance.disabledDateRanges.set(this.disabledDateRanges() ?? [])
    instance.silentDisabledDateRanges.set(this.silentDisabledDateRanges() ?? [])
    instance.allowedWeekDays.set(this.allowedWeekDays() ?? null)
    instance.compactCalendarOnMobile.set(Boolean(options?.forceSingleMonth) || this.compactCalendarOnMobile())
    instance.onApply = (range: DateRange) => {
      this.rangeSelected.emit(range)
      this._closePickerUi()
    }
    instance.closeDropdown = () => this._closePickerUi()
  }

  get contentSetter(): ComponentWithDataSetter<DateRangePickerContentComponent> {
    return {
      component: DateRangePickerContentComponent,
      dataSetter: (instance: DateRangePickerContentComponent) => {
        this._wirePickerContentInstance(instance)
        instance.init()
      }
    }
  }
}
